<template>
    <div class="similar-image-review-page public_box">
        <div class="similar-image-review-page__header">
            <div>
                <div class="similar-image-review-page__nav">
                    <el-button size="mini" plain @click="goWorkbench">返回核验工作台</el-button>
                    <el-button size="mini" type="text" @click="goDuplicateReview">切换到重复图片核验</el-button>
                </div>
                <h2>相似图核验页面</h2>
                <p>集中查看高相似候选图，完成确认有效、确认排除或待定处理。</p>
            </div>
            <div class="similar-image-review-page__filters">
                <el-switch
                    v-model="filters.onlyPending"
                    active-text="只看待复核"
                    @input="handleOnlyPendingToggle"
                />
                <el-radio-group
                    v-model="filters.reviewStatus"
                    size="mini"
                    :disabled="filters.onlyPending"
                    @input="handleReviewStatusChange"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="confirmed-valid">确认有效</el-radio-button>
                    <el-radio-button label="confirmed-rejected">确认排除</el-radio-button>
                    <el-radio-button label="all">全部</el-radio-button>
                </el-radio-group>
                <el-select
                    v-model="filters.llmCategoryLevel1"
                    clearable
                    size="mini"
                    placeholder="按一级分类筛选"
                    class="similar-image-review-page__category-select"
                    @change="handleCategoryChange"
                    @clear="handleCategoryChange"
                >
                    <el-option
                        v-for="item in llmCategoryOptions"
                        :key="item"
                        :label="item"
                        :value="item"
                    />
                </el-select>
                <div class="similar-image-review-page__similarity-filter">
                    <span class="similar-image-review-page__filter-label">相似度区间</span>
                    <el-input-number
                        v-model="filters.similarityScoreMin"
                        :min="0"
                        :max="0.9999"
                        :step="0.001"
                        :precision="3"
                        size="mini"
                        controls-position="right"
                        @change="handleSimilarityFilterChange"
                    />
                    <span class="similar-image-review-page__filter-separator">&lt; score &lt;</span>
                    <el-input-number
                        v-model="filters.similarityScoreMax"
                        :min="0.0001"
                        :max="1"
                        :step="0.001"
                        :precision="3"
                        size="mini"
                        controls-position="right"
                        @change="handleSimilarityFilterChange"
                    />
                </div>
                <el-select
                    v-model="filters.similarityScoreOrder"
                    size="mini"
                    placeholder="相似度排序"
                    class="similar-image-review-page__sort-select"
                    @change="handleSimilarityFilterChange"
                >
                    <el-option label="相似度倒序" value="desc" />
                    <el-option label="相似度正序" value="asc" />
                </el-select>
            </div>
        </div>

        <el-alert
            v-if="errorMessage"
            type="error"
            :closable="false"
            :title="errorMessage"
            class="similar-image-review-page__alert"
        />

        <el-empty
            v-if="!loading && !records.length"
            description="当前无可核验记录"
        />

        <div v-if="records.length" class="similar-image-review-page__batch-bar">
            <div class="similar-image-review-page__batch-summary">
                当前页共 {{ records.length }} 条，可一键批量判断
            </div>
            <div class="similar-image-review-page__batch-actions">
                <el-radio-group
                    v-model="batchDecision"
                    size="mini"
                    class="similar-image-review-page__batch-group"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="confirmed-valid">确认有效</el-radio-button>
                    <el-radio-button label="confirmed-rejected">确认排除</el-radio-button>
                </el-radio-group>
                <el-button
                    size="mini"
                    type="primary"
                    :loading="batchSaving"
                    @click="handleBatchSaveConfirm"
                >
                    当前页批量保存
                </el-button>
            </div>
        </div>

        <div v-loading="loading" class="similar-image-review-page__list">
            <similar-image-review-card
                v-for="record in records"
                :key="record.reviewRecordId"
                :record="record"
                @open-source-image="handleOpenWorkbench(record, 'source')"
                @open-target-image="handleOpenWorkbench(record, 'target')"
            >
                <template #actions>
                    <similar-image-review-decision-panel
                        v-model="draftStatusMap[record.reviewRecordId]"
                        :saving="savingRecordId === record.reviewRecordId"
                        @save="handleSave(record)"
                    />
                </template>
            </similar-image-review-card>
        </div>

        <div class="similar-image-review-page__pagination">
            <el-pagination
                background
                layout="prev, pager, next"
                :current-page="filters.pageNo"
                :page-size="filters.pageSize"
                :total="total"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script>
import SimilarImageReviewCard from "@/components/medical/SimilarImageReviewCard.vue";
import SimilarImageReviewDecisionPanel from "@/components/medical/SimilarImageReviewDecisionPanel.vue";
import { buildDuplicateCaseJumpUrl } from "@/views/calculatorTool/medicalThreeColumn.duplicateCase";
import {
    getSimilarImageReviewRecords,
    saveSimilarImageReviewBatchDecision,
    saveSimilarImageReviewDecision,
} from "@/api/similarImageReview";

export default {
    name: "SimilarImageReviewPage",
    data() {
        return {
            loading: false,
            savingRecordId: null,
            records: [],
            total: 0,
            errorMessage: "",
            draftStatusMap: {},
            batchDecision: "confirmed-valid",
            batchSaving: false,
            llmCategoryOptions: ["医疗类", "物损类", "单据类", "现场类", "证件类", "其他"],
            filters: {
                reviewStatus: "pending",
                onlyPending: true,
                llmCategoryLevel1: "",
                similarityScoreMin: 0.97,
                similarityScoreMax: 1,
                similarityScoreOrder: "desc",
                pageNo: 1,
                pageSize: 20,
            },
        };
    },
    components: {
        SimilarImageReviewCard,
        SimilarImageReviewDecisionPanel,
    },
    inject: {
        injectedInterfaceUrl: {
            from: "interfaceUrl",
            default: "",
        },
    },
    created() {
        this.hydrateFiltersFromRoute();
        this.fetchRecords();
    },
    methods: {
        getResolvedInterfaceUrl() {
            return this.injectedInterfaceUrl || "";
        },
        goWorkbench() {
            this.$router.push({ path: "/calculatorTool/reviewWorkbench" });
        },
        goDuplicateReview() {
            this.$router.push({ path: "/calculatorTool/duplicateReview" });
        },
        hydrateFiltersFromRoute() {
            const query = (this.$route && this.$route.query) || {};
            const onlyPending = query.onlyPending !== "false";
            this.filters.onlyPending = onlyPending;
            this.filters.reviewStatus = onlyPending ? "pending" : query.reviewStatus || "all";
            this.filters.llmCategoryLevel1 = query.llmCategoryLevel1 || "";
            this.filters.similarityScoreMin = this.parseSimilarityValue(query.similarityScoreMin, 0.97);
            this.filters.similarityScoreMax = this.parseSimilarityValue(query.similarityScoreMax, 1);
            this.filters.similarityScoreOrder = this.parseSimilarityOrder(query.similarityScoreOrder);
            this.filters.pageNo = Number(query.pageNo) > 0 ? Number(query.pageNo) : 1;
        },
        parseSimilarityValue(value, fallback) {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : fallback;
        },
        parseSimilarityOrder(value) {
            return value === "asc" ? "asc" : "desc";
        },
        syncRouteQuery() {
            if (!this.$router || !this.$route) {
                return;
            }
            this.$router.replace({
                query: {
                    onlyPending: this.filters.onlyPending ? "true" : "false",
                    reviewStatus: this.filters.reviewStatus,
                    llmCategoryLevel1: this.filters.llmCategoryLevel1 || undefined,
                    similarityScoreMin: this.filters.similarityScoreMin,
                    similarityScoreMax: this.filters.similarityScoreMax,
                    similarityScoreOrder: this.filters.similarityScoreOrder || undefined,
                    pageNo: this.filters.pageNo > 1 ? String(this.filters.pageNo) : undefined,
                },
            }).catch(() => {});
        },
        openWorkbenchWithFocus(payload = {}) {
            const caseNo = String(payload.caseNo || "").trim();
            const focusMaterialId = payload.focusMaterialId;
            const focusFileHash = String(payload.focusFileHash || "").trim();
            const focusMaterialType = String(payload.focusMaterialType || "").trim();
            if (!caseNo || (!focusMaterialId && !focusFileHash)) {
                this.$message.warning("当前图片缺少定位信息，暂不可跳转");
                return;
            }
            const nextUrl = buildDuplicateCaseJumpUrl("/calculatorTool/medicalThreeColumn", {
                caseNo,
                focusMaterialId,
                focusFileHash,
                focusMaterialType,
            });
            window.open(nextUrl, "_blank");
        },
        handleOpenWorkbench(record, side) {
            if (!record) {
                return;
            }
            if (side === "source") {
                this.openWorkbenchWithFocus({
                    caseNo: record.sourceCaseNo,
                    focusMaterialId: record.sourceImageId,
                    focusFileHash: record.sourceFileHash,
                });
                return;
            }
            this.openWorkbenchWithFocus({
                caseNo: record.targetCaseNo,
                focusMaterialId: record.targetImageId,
                focusFileHash: record.targetFileHash || record.hitObjectId,
            });
        },
        async fetchRecords() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await getSimilarImageReviewRecords(
                    this.getResolvedInterfaceUrl(),
                    {
                        ...this.filters,
                        reviewStatus: this.filters.onlyPending ? "pending" : this.filters.reviewStatus,
                    },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("相似图核验记录加载失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "相似图核验记录加载失败");
                }
                const payload = root.data || {};
                this.records = Array.isArray(payload.items) ? payload.items : [];
                this.total = Number(payload.total || 0) || 0;
                this.draftStatusMap = this.records.reduce((acc, item) => {
                    acc[item.reviewRecordId] = item.reviewStatus || "pending";
                    return acc;
                }, {});
            } catch (error) {
                this.records = [];
                this.total = 0;
                this.errorMessage = error && error.message ? error.message : "相似图核验记录加载失败";
            } finally {
                this.loading = false;
            }
        },
        handleReviewStatusChange(value) {
            this.filters.reviewStatus = value;
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleOnlyPendingToggle(value) {
            this.filters.onlyPending = Boolean(value);
            this.filters.reviewStatus = this.filters.onlyPending ? "pending" : "all";
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handlePageChange(pageNo) {
            this.filters.pageNo = pageNo;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleCategoryChange() {
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleSimilarityFilterChange() {
            if (Number(this.filters.similarityScoreMin) >= Number(this.filters.similarityScoreMax)) {
                this.$message.warning("相似度下限必须小于上限");
                return;
            }
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleBatchSaveConfirm() {
            if (!this.records.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decisionLabelMap = {
                pending: "待定",
                "confirmed-valid": "确认有效",
                "confirmed-rejected": "确认排除",
            };
            const decisionLabel = decisionLabelMap[this.batchDecision] || this.batchDecision || "待定";
            this.$confirm(
                `确认将当前页 ${this.records.length} 条记录统一标记为「${decisionLabel}」吗？`,
                "批量保存确认",
                {
                    confirmButtonText: "确认保存",
                    cancelButtonText: "取消",
                    type: "warning",
                }
            )
                .then(() => this.handleBatchSave())
                .catch(() => {});
        },
        async handleSave(record) {
            const decision = this.draftStatusMap[record.reviewRecordId] || "pending";
            this.savingRecordId = record.reviewRecordId;
            try {
                const response = await saveSimilarImageReviewDecision(
                    this.getResolvedInterfaceUrl(),
                    record.reviewRecordId,
                    { decision },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("保存失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "保存失败");
                }
                const latest = root.data || {};
                const normalizedRecords = this.records.map((item) =>
                    item.reviewRecordId === record.reviewRecordId
                        ? { ...item, ...latest, reviewStatus: decision }
                        : item
                );
                this.draftStatusMap = {
                    ...this.draftStatusMap,
                    [record.reviewRecordId]: decision,
                };
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = normalizedRecords.filter(
                        (item) => item.reviewRecordId !== record.reviewRecordId
                    );
                    this.total = Math.max(0, Number(this.total || 0) - 1);
                    if (!this.records.length && this.total > 0) {
                        await this.fetchRecords();
                    }
                } else {
                    this.records = normalizedRecords;
                }
                this.$message.success("判断结果已保存");
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "保存失败");
            } finally {
                this.savingRecordId = null;
            }
        },
        async handleBatchSave() {
            const reviewRecordIds = this.records
                .map((item) => item && item.reviewRecordId)
                .filter((item) => Number(item) > 0);
            if (!reviewRecordIds.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decision = this.batchDecision || "pending";
            this.batchSaving = true;
            try {
                const response = await saveSimilarImageReviewBatchDecision(
                    this.getResolvedInterfaceUrl(),
                    {
                        reviewRecordIds,
                        decision,
                    },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("批量保存失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "批量保存失败");
                }
                this.draftStatusMap = reviewRecordIds.reduce((acc, reviewRecordId) => {
                    acc[reviewRecordId] = decision;
                    return acc;
                }, { ...this.draftStatusMap });
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = [];
                    this.total = Math.max(0, Number(this.total || 0) - reviewRecordIds.length);
                    await this.fetchRecords();
                } else {
                    this.records = this.records.map((item) => ({
                        ...item,
                        reviewStatus: reviewRecordIds.includes(item.reviewRecordId) ? decision : item.reviewStatus,
                    }));
                }
                const updatedCount = Number(root?.data?.updatedCount || reviewRecordIds.length) || reviewRecordIds.length;
                this.$message.success(`已批量保存 ${updatedCount} 条判断结果`);
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "批量保存失败");
            } finally {
                this.batchSaving = false;
            }
        },
    },
};
</script>

<style scoped>
.similar-image-review-page {
    padding: 20px;
}

.similar-image-review-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}

.similar-image-review-page__filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.similar-image-review-page__nav {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.similar-image-review-page__category-select {
    width: 180px;
}

.similar-image-review-page__similarity-filter {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.similar-image-review-page__filter-label,
.similar-image-review-page__filter-separator {
    color: #606266;
    font-size: 12px;
    white-space: nowrap;
}

.similar-image-review-page__sort-select {
    width: 132px;
}

.similar-image-review-page__header h2 {
    margin: 0;
    color: #1f2d3d;
}

.similar-image-review-page__header p {
    margin: 8px 0 0;
    color: #606266;
    max-width: 680px;
    line-height: 1.6;
}

.similar-image-review-page__alert {
    margin-bottom: 16px;
}

.similar-image-review-page__batch-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 16px;
    margin-bottom: 16px;
    background: #f7f9fc;
    border: 1px solid #e4eaf3;
    border-radius: 12px;
}

.similar-image-review-page__batch-summary {
    color: #4f5d75;
    font-size: 13px;
}

.similar-image-review-page__batch-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.similar-image-review-page__batch-group {
    flex-wrap: wrap;
}

.similar-image-review-page__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.similar-image-review-page__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
}

@media (max-width: 768px) {
    .similar-image-review-page__header {
        flex-direction: column;
    }

    .similar-image-review-page__filters {
        width: 100%;
        justify-content: flex-start;
    }

    .similar-image-review-page__batch-bar {
        flex-direction: column;
        align-items: flex-start;
    }

    .similar-image-review-page__batch-actions {
        width: 100%;
        justify-content: flex-start;
    }

    .similar-image-review-page__category-select {
        width: 100%;
    }
}
</style>
